// Shared rules for CRM › Outreach (lists, campaigns, the reply agent). The plan is VendorReachout.md.
const VENDOR_STATUSES = ['Unverified', 'Verified', 'Not interested'];
const MEMBER_STAGES = ['not_contacted', 'emailed', 'followed_up', 'replied', 'stopped', 'no_email', 'opted_out'];
const STAGE_LABELS = { not_contacted: 'Not contacted', emailed: 'Emailed', followed_up: 'Followed up', replied: 'Replied', stopped: 'Stopped', no_email: 'No email or WhatsApp', opted_out: 'Opted out' };
const DRAFT_LABELS = ['interested', 'question', 'price', 'decline', 'out_of_office', 'unclear'];
const DOC_KINDS = { main: ['brief'], project: ['description', 'pricing'] };
const IMPORT_FIELDS = ['company', 'name', 'email', 'phone', 'title', 'city', 'website', 'notes'];
const EMAIL = /^[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+$/;

const vendorStatus = company => VENDOR_STATUSES.includes(company?.vendorStatus) ? company.vendorStatus : 'Unverified';
// Vendors that arrived through an Outreach import stay off the main CRM until they are Verified.
// Vendors that were in the CRM before Outreach existed carry no origin and stay visible.
const hiddenFromMainCRM = company => Boolean(company?.outreachOrigin) && company.vendorStatus !== 'Verified';
// The main CRM page without those vendors, their people and their timelines (they live on CRM › Outreach).
function mainCRMView(data) {
    const hidden = new Set((data.companies || []).filter(hiddenFromMainCRM).map(company => company.id));
    if (!hidden.size) return data;
    const people = new Set((data.contacts || []).filter(contact => hidden.has(contact.companyId)).map(contact => contact.id));
    const shown = activity => !(activity.recordType === 'companies' && hidden.has(activity.recordId)) && !(activity.recordType === 'contacts' && people.has(activity.recordId));
    return { ...data, companies: data.companies.filter(company => !hidden.has(company.id)), contacts: data.contacts.filter(contact => !people.has(contact.id)),
        activities: data.activities.filter(shown), outreachHidden: hidden.size };
}

const HEADER_ALIASES = {
    company: ['company', 'company name', 'vendor', 'vendor name', 'organisation', 'organization', 'business', 'studio'],
    name: ['name', 'contact', 'contact name', 'person', 'full name', 'first name'],
    email: ['email', 'email address', 'e-mail', 'mail'],
    phone: ['phone', 'phone number', 'mobile', 'contact number', 'whatsapp', 'number'],
    title: ['title', 'designation', 'role', 'job title'],
    city: ['city', 'location', 'town'],
    website: ['website', 'site', 'url', 'domain', 'instagram', 'link'],
    notes: ['notes', 'note', 'remarks', 'comments', 'description', 'services', 'speciality', 'specialty'],
};
const fieldFor = header => {
    const value = String(header || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
    return Object.keys(HEADER_ALIASES).find(field => HEADER_ALIASES[field].includes(value)) || '';
};
// Turns parsed CSV/XLSX rows (first row = headers) or JSON objects into import rows.
// Also reads the CRM JSON import file ({ companies: [...], contacts: [...] }).
function importRows(input) {
    const clean = value => String(value ?? '').replace(/\s+/g, ' ').trim().slice(0, 500);
    let objects = [];
    if (input && !Array.isArray(input) && typeof input === 'object') {
        const companies = Array.isArray(input.companies) ? input.companies.filter(item => item && typeof item === 'object') : [];
        const people = Array.isArray(input.contacts) ? input.contacts : Array.isArray(input.vendors) ? input.vendors : [];
        const nameOf = company => company.name || company.company_name || '';
        const find = reference => companies.find(company => [company.id, nameOf(company), company.domain].some(value => value !== undefined && value !== '' && String(value).toLowerCase() === String(reference ?? '').toLowerCase()));
        const used = new Set();
        const rows = people.filter(item => item && typeof item === 'object').map(person => {
            const company = find(person.company ?? person.companyId ?? person.company_id);
            if (company) used.add(company);
            return { ...person, company: company ? nameOf(company) : typeof person.company === 'string' ? person.company : '', website: person.website || company?.website || company?.domain || '', city: person.city || company?.city || company?.location || '' };
        });
        return importRows([...rows, ...companies.filter(company => !used.has(company)).map(company => ({ company: nameOf(company), website: company.website || company.domain || '', city: company.city || company.location || '', phone: company.phone || '', email: company.email || '', notes: company.description || '' }))]);
    }
    if (Array.isArray(input) && Array.isArray(input[0])) {
        const fields = input[0].map(fieldFor);
        objects = input.slice(1).map(row => Object.fromEntries(fields.map((field, index) => [field, row[index]]).filter(([field]) => field)));
    } else if (Array.isArray(input)) {
        objects = input.map(item => {
            if (!item || typeof item !== 'object') return {};
            const flat = { ...item, ...(item.contact && typeof item.contact === 'object' ? item.contact : {}) };
            if (typeof item.company === 'object' && item.company) flat.company = item.company.name;
            const row = {};
            for (const [key, value] of Object.entries(flat)) { const field = fieldFor(key); if (field && !row[field] && typeof value !== 'object') row[field] = value; }
            return row;
        });
    }
    return objects.map(row => Object.fromEntries(IMPORT_FIELDS.map(field => [field, clean(row[field])]).filter(([, value]) => value)))
        .map(row => ({ ...row, email: (row.email || '').toLowerCase() }))
        .filter(row => row.company || row.name || row.email);
}
function rowProblem(row) {
    if (!row.company && !row.name) return 'Add a company or contact name.';
    if (row.email && !EMAIL.test(row.email)) return 'Email address is not valid.';
    return '';
}

module.exports = { VENDOR_STATUSES, MEMBER_STAGES, STAGE_LABELS, DRAFT_LABELS, DOC_KINDS, IMPORT_FIELDS, EMAIL, vendorStatus, hiddenFromMainCRM, mainCRMView, importRows, rowProblem };
